/**
 * Phase 17 — small local avatar. Renders the uploaded profile photo when
 * `photoUrl` is present, otherwise a default initials avatar (first letters
 * of the name on a forest-tinted paper circle).
 *
 * Kept local to apps/portal (mirrored in apps/admin) rather than in
 * @truecare/ui to avoid the shared-ui rebuild dance for a ~30-LOC component.
 */
export function Avatar({
  photoUrl,
  name,
  size = 64,
}: {
  photoUrl?: string | null;
  name: string;
  size?: number;
}) {
  const dimension = { width: size, height: size };
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name}
        width={size}
        height={size}
        style={dimension}
        className="rounded-full object-cover ring-1 ring-forest/15"
      />
    );
  }
  return (
    <div
      style={{ ...dimension, fontSize: Math.round(size * 0.4) }}
      className="flex select-none items-center justify-center rounded-full bg-forest/10 font-heading font-semibold uppercase text-forest ring-1 ring-forest/15"
      aria-label={name}
      role="img"
    >
      {initials(name)}
    </div>
  );
}

/** First letters of up to the first two whitespace-separated name parts. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
